import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAcceptInvite, useCheckInvite } from '@/api/auth';
import { useAuth } from '@/hooks/useAuth';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Label } from '@/components/ui/Label';
import { Spinner } from '@/components/ui/Spinner';
import { PasswordStrengthMeter } from '@/components/ui/PasswordStrengthMeter';
import { apiError } from '@/lib/api';
import { BillerBayLogo } from '@/components/ui/BillerBayLogo';
import { ShieldCheck, Building2 } from 'lucide-react';

export function AcceptInvitePage() {
  usePageTitle('Accept invitation');
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const check = useCheckInvite(token);
  const accept = useAcceptInvite();
  const { setSession, user } = useAuth();
  const navigate = useNavigate();

  // New-user form state
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);

  const passwordsMatch = !confirm || password === confirm;

  const handleNewUserSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    try {
      const resp = await accept.mutateAsync({ token, password, full_name: fullName });
      if (resp.mfa_setup_required && resp.setup_token) {
        sessionStorage.setItem('mfa_setup_token', resp.setup_token);
        navigate('/mfa-setup-required', { replace: true });
        return;
      }
      setSession(resp);
      navigate('/', { replace: true });
    } catch (err) {
      setError(apiError(err));
    }
  };

  const handleExistingUserAccept = async () => {
    setError(null);
    try {
      const resp = await accept.mutateAsync({ token });
      setSession(resp);
      navigate('/', { replace: true });
    } catch (err) {
      setError(apiError(err));
    }
  };

  const orgName = check.data?.org_name;
  const userExists = check.data?.user_exists ?? false;
  const alreadyAccepted = check.data?.already_accepted ?? false;
  const expired = check.data?.expired ?? false;
  // Logged-in user's email matches the invite email
  const isLoggedInMatch = !!user && check.data && user.email.toLowerCase() === check.data.email.toLowerCase();

  return (
    <div className="min-h-screen flex bg-[#F2F2F8] dark:bg-[#0D0D1A]">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-[420px] shrink-0 bg-[#12122C] p-10">
        <BillerBayLogo variant="light" height={26} />
        <div>
          <h1 className="text-3xl font-bold text-white leading-snug">
            {userExists
              ? `Join ${orgName ?? 'a new organisation'} on BillerBay Exchange.`
              : "You've been invited to BillerBay Exchange."}
          </h1>
          <p className="mt-4 text-sm text-white/60 leading-relaxed">
            {userExists
              ? 'Accept the invitation to access this organisation with your existing account.'
              : 'Set your password to activate your account and start exchanging files securely with your team.'}
          </p>
          <div className="mt-8 flex items-center gap-2 text-sm text-white/40">
            <ShieldCheck size={14} className="text-[#00CBDE]" />
            <span>Your information is stored securely and reviewed by authorized BillerBay staff.</span>
          </div>
        </div>
        <div className="text-[11px] text-white/20">
          © {new Date().getFullYear()} BillerBay · Exchange
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex justify-center">
            <BillerBayLogo variant="dark" height={28} />
          </div>

          <div className="bg-white dark:bg-[#1A1A2E] border border-[#E3E3F1] dark:border-[#2A2A4A] rounded-xl shadow-[0_1px_2px_rgba(15,23,42,0.06)] p-8">

            {/* Loading state */}
            {check.isLoading && (
              <div className="flex items-center justify-center py-8">
                <Spinner />
              </div>
            )}

            {/* Already accepted */}
            {check.data && alreadyAccepted && (
              <>
                <h2 className="text-xl font-semibold text-[#12122C] dark:text-white mb-2">Invitation already used</h2>
                <p className="text-sm text-[#676687] dark:text-[#8888AA] mb-5">
                  This invitation link has already been accepted
                  {orgName ? ` for ${orgName}` : ''}. If you need access, sign in to your existing account or contact your administrator.
                </p>
                <Link to="/login" className="block w-full">
                  <Button variant="primary" className="w-full justify-center py-2.5 text-sm">
                    Go to sign in
                  </Button>
                </Link>
              </>
            )}

            {/* Expired */}
            {check.data && expired && (
              <>
                <h2 className="text-xl font-semibold text-[#12122C] dark:text-white mb-2">Invitation expired</h2>
                <p className="text-sm text-[#676687] dark:text-[#8888AA] mb-5">
                  This invitation link has expired. Please ask your administrator to send a new invite.
                </p>
                <Link to="/login" className="block w-full">
                  <Button variant="primary" className="w-full justify-center py-2.5 text-sm">
                    Go to sign in
                  </Button>
                </Link>
              </>
            )}

            {/* Invalid / unknown token */}
            {(check.isError || !token) && !check.isLoading && (
              <>
                <h2 className="text-xl font-semibold text-[#12122C] dark:text-white mb-2">Invalid invitation</h2>
                <div className="text-sm text-[#B91C1C] bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-4 py-3 mb-5">
                  {!token ? 'No invite token found in the link.' : apiError(check.error)}
                </div>
                <Link to="/login" className="block w-full">
                  <Button variant="secondary" className="w-full justify-center py-2.5 text-sm">
                    Go to sign in
                  </Button>
                </Link>
              </>
            )}

            {/* Existing user flow */}
            {check.data && userExists && !alreadyAccepted && !expired && (
              <>
                <div className="mb-6">
                  <h2 className="text-xl font-semibold text-[#12122C] dark:text-white">Accept invitation</h2>
                  <p className="text-sm text-[#676687] dark:text-[#8888AA] mt-1">
                    You already have a BillerBay Exchange account.
                  </p>
                </div>

                {orgName && (
                  <div className="flex items-center gap-3 p-3 mb-5 bg-[#F5F5FF] dark:bg-[#1E1E35] border border-[#E3E3F1] dark:border-[#2A2A4A] rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-[#EEF0FF] dark:bg-[#2A2A4A] flex items-center justify-center shrink-0">
                      <Building2 size={16} className="text-[#3F4CFF]" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 dark:text-[#8888AA]">You're being invited to</p>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">{orgName}</p>
                    </div>
                  </div>
                )}

                {/* Already logged in as the right user */}
                {isLoggedInMatch ? (
                  <>
                    <p className="text-sm text-slate-600 dark:text-[#8888AA] mb-4">
                      Signed in as <strong>{user!.email}</strong>. Click below to accept.
                    </p>
                    {error && (
                      <div className="text-sm text-[#B91C1C] bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-4 py-3 mb-4">
                        {error}
                      </div>
                    )}
                    <Button
                      variant="primary"
                      loading={accept.isPending}
                      disabled={accept.isPending}
                      className="w-full justify-center py-2.5 text-sm"
                      onClick={handleExistingUserAccept}
                    >
                      Accept &amp; join {orgName ?? 'organisation'}
                    </Button>
                  </>
                ) : (
                  /* Not logged in or logged in as a different user */
                  <>
                    <p className="text-sm text-slate-600 dark:text-[#8888AA] mb-5">
                      Sign in with <strong>{check.data.email}</strong> to accept this invitation.
                    </p>
                    <Link
                      to={`/login?from=${encodeURIComponent(`/accept-invite?token=${token}`)}`}
                      className="block w-full"
                    >
                      <Button variant="primary" className="w-full justify-center py-2.5 text-sm">
                        Sign in to accept
                      </Button>
                    </Link>
                  </>
                )}
              </>
            )}

            {/* New user flow */}
            {check.data && !userExists && !alreadyAccepted && !expired && (
              <>
                <div className="mb-6">
                  <h2 className="text-xl font-semibold text-[#12122C] dark:text-white">Activate your account</h2>
                  <p className="text-sm text-[#676687] dark:text-[#8888AA] mt-1">Create your password to get started.</p>
                </div>

                <form onSubmit={handleNewUserSubmit} className="space-y-4" noValidate>
                  <div>
                    <Label htmlFor="full_name">Full name</Label>
                    <Input
                      id="full_name"
                      autoComplete="name"
                      autoFocus
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="Jane Smith"
                    />
                  </div>
                  <div>
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="At least 8 characters"
                    />
                    {password && <PasswordStrengthMeter password={password} />}
                  </div>
                  <div>
                    <Label htmlFor="confirm">Confirm password</Label>
                    <Input
                      id="confirm"
                      type="password"
                      autoComplete="new-password"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      invalid={!!confirm && !passwordsMatch}
                    />
                    {!!confirm && !passwordsMatch && (
                      <p className="text-xs text-[#B91C1C] mt-1">Passwords do not match.</p>
                    )}
                  </div>

                  {error && (
                    <div className="text-sm text-[#B91C1C] bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-4 py-3">
                      {error}
                    </div>
                  )}

                  <Button
                    type="submit"
                    variant="primary"
                    loading={accept.isPending}
                    disabled={!fullName.trim() || password.length < 8 || password !== confirm || accept.isPending}
                    className="w-full justify-center py-2.5 text-sm"
                  >
                    {accept.isPending ? 'Activating…' : 'Activate account'}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
